import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { theme } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { loadedInitSelector, locationSelector, mobileViewSelector } from 'app-redux/selectors'
import useMemoizeCheckPermission from 'utils/hooks/useMemoizeCheckPermission'
import { useLowStockSummaryQuery } from 'utils/queries/lowStock'
import DetailDrawer from './DetailDrawer'
import { buildLowStockCssVars } from './cssVars'
import './index.scss'

export const BoxAlertIllustration = () => (
  <svg viewBox="0 0 48 48" fill="none" width="30" height="30">
    <path
      d="M6 16 24 8l18 8-18 8-18-8Z"
      fill="var(--ls-warn-bg)"
      stroke="var(--ls-warn)"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <path
      d="M6 16v16l18 8V24"
      fill="none"
      stroke="var(--ls-warn-text)"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <path d="M42 16v9" stroke="var(--ls-warn-text)" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="37" cy="34" r="9" fill="var(--ls-crit)" />
    <path d="M37 30v5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="37" cy="38.4" r="1.35" fill="#fff" />
  </svg>
)

const LowStockBanner = () => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()

  const loadedInit = useSelector(loadedInitSelector)
  const mobileView = useSelector(mobileViewSelector)
  const location = useSelector(locationSelector)
  const canViewProducts = useMemoizeCheckPermission({ permission: 'products' })

  const isInvoicesPage = /^\/sales\/invoices\/?$/.test(location?.pathname || '')

  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const { data } = useLowStockSummaryQuery({
    enabled: loadedInit && canViewProducts && isInvoicesPage,
  })

  const cssVars = useMemo(() => buildLowStockCssVars(token), [token])

  const almostEmptyCount = data?.almost_empty?.count || 0
  const emptyCount = data?.empty?.count || 0
  const total = useMemo(() => almostEmptyCount + emptyCount, [almostEmptyCount, emptyCount])

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])
  const handleDismiss = useCallback(() => setDismissed(true), [])

  if (!isInvoicesPage || !canViewProducts || dismissed || total === 0) {
    return null
  }

  return (
    <>
      <div className="low-stock-banner hide-on-print" style={cssVars} role="status">
        <span className="low-stock-banner__illus">
          <BoxAlertIllustration />
        </span>
        <div className="low-stock-banner__body">
          <div className="low-stock-banner__title">
            {formatMessage({ id: 'lowStock.attention_title' })}
          </div>
          <div className="low-stock-banner__summary">
            {almostEmptyCount > 0 && (
              <span className="is-warn">
                {formatMessage(
                  { id: 'lowStock.summary_almost_empty' },
                  { count: almostEmptyCount },
                )}
              </span>
            )}
            {almostEmptyCount > 0 && emptyCount > 0 && <span className="sep">, </span>}
            {emptyCount > 0 && (
              <span className="is-crit">
                {formatMessage({ id: 'lowStock.summary_empty' }, { count: emptyCount })}
              </span>
            )}
          </div>
        </div>
        <div className="low-stock-banner__actions">
          <button
            type="button"
            className="low-stock-banner__close"
            aria-label="Close"
            onClick={handleDismiss}
          >
            <CloseOutlined />
          </button>
          <button type="button" className="low-stock-banner__detail-btn" onClick={handleOpen}>
            {formatMessage({ id: 'lowStock.view_detail' })}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              width="14"
              height="14"
            >
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <DetailDrawer open={open} onClose={handleClose} data={data} mobileView={mobileView} />
    </>
  )
}

export default LowStockBanner
